import { useMemo } from 'react';
import {
  Table,
  Card,
  Typography,
  Spin,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  DollarOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useArSummary } from '@hooks/useFinance';
import type { ArSummaryItem } from '@/types/finance';

export default function ArDashboardPage() {
  const { data: mdArData, isLoading: mdIsLoading } = useArSummary();

  const mdTotals = useMemo(() => {
    if (!mdArData || mdArData.length === 0) {
      return { mdTotalDebt: 0, mdCurrentDebt: 0, mdOverdueDebt: 0 };
    }
    const mdTotalDebt = mdArData.reduce((mdSum, mdItem) => mdSum + mdItem.totalOutstanding, 0);
    const mdCurrentDebt = mdArData.reduce((mdSum, mdItem) => mdSum + mdItem.current, 0);
    const mdOverdueDebt = mdArData.reduce(
      (mdSum, mdItem) => mdSum + mdItem.days1To30 + mdItem.days31To60 + mdItem.days61To90 + mdItem.days90Plus,
      0
    );
    return { mdTotalDebt, mdCurrentDebt, mdOverdueDebt };
  }, [mdArData]);

  const mdAgingDistribution = useMemo(() => {
    if (!mdArData || mdArData.length === 0) return [];
    const mdCurrent = mdArData.reduce((mdS, mdI) => mdS + mdI.current, 0);
    const md30 = mdArData.reduce((mdS, mdI) => mdS + mdI.days1To30, 0);
    const md60 = mdArData.reduce((mdS, mdI) => mdS + mdI.days31To60, 0);
    const md90 = mdArData.reduce((mdS, mdI) => mdS + mdI.days61To90, 0);
    const md90Plus = mdArData.reduce((mdS, mdI) => mdS + mdI.days90Plus, 0);
    const mdTotal = mdCurrent + md30 + md60 + md90 + md90Plus;
    if (mdTotal === 0) return [];
    return [
      { label: 'Поточна', amount: mdCurrent, color: '#52c41a', percent: (mdCurrent / mdTotal) * 100 },
      { label: '1–30 днів', amount: md30, color: '#faad14', percent: (md30 / mdTotal) * 100 },
      { label: '31–60 днів', amount: md60, color: '#fa8c16', percent: (md60 / mdTotal) * 100 },
      { label: '61–90 днів', amount: md90, color: '#f5222d', percent: (md90 / mdTotal) * 100 },
      { label: '90+ днів', amount: md90Plus, color: '#8b0000', percent: (md90Plus / mdTotal) * 100 },
    ];
  }, [mdArData]);

  const mdColumns: ColumnsType<ArSummaryItem> = [
    {
      title: 'Клієнт',
      dataIndex: 'customerName',
      key: 'customerName',
      ellipsis: true,
      sorter: (mdA, mdB) => mdA.customerName.localeCompare(mdB.customerName),
    },
    {
      title: 'Поточна',
      dataIndex: 'current',
      key: 'current',
      width: 140,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
      sorter: (mdA, mdB) => mdA.current - mdB.current,
    },
    {
      title: '1–30 днів',
      dataIndex: 'days1To30',
      key: 'days1To30',
      width: 140,
      align: 'right',
      render: (mdValue: number) => (
        <Typography.Text type={mdValue > 0 ? 'warning' : undefined}>
          {mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
        </Typography.Text>
      ),
      sorter: (mdA, mdB) => mdA.days1To30 - mdB.days1To30,
    },
    {
      title: '31–60 днів',
      dataIndex: 'days31To60',
      key: 'days31To60',
      width: 140,
      align: 'right',
      render: (mdValue: number) => (
        <Typography.Text type={mdValue > 0 ? 'warning' : undefined}>
          {mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
        </Typography.Text>
      ),
      sorter: (mdA, mdB) => mdA.days31To60 - mdB.days31To60,
    },
    {
      title: '61–90 днів',
      dataIndex: 'days61To90',
      key: 'days61To90',
      width: 140,
      align: 'right',
      render: (mdValue: number) => (
        <Typography.Text type={mdValue > 0 ? 'danger' : undefined}>
          {mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
        </Typography.Text>
      ),
      sorter: (mdA, mdB) => mdA.days61To90 - mdB.days61To90,
    },
    {
      title: '90+ днів',
      dataIndex: 'days90Plus',
      key: 'days90Plus',
      width: 140,
      align: 'right',
      render: (mdValue: number) => (
        <Typography.Text type={mdValue > 0 ? 'danger' : undefined} strong={mdValue > 0}>
          {mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
        </Typography.Text>
      ),
      sorter: (mdA, mdB) => mdA.days90Plus - mdB.days90Plus,
    },
    {
      title: 'Всього',
      dataIndex: 'totalOutstanding',
      key: 'totalOutstanding',
      width: 150,
      align: 'right',
      render: (mdValue: number) => (
        <Typography.Text strong>
          {mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
        </Typography.Text>
      ),
      sorter: (mdA, mdB) => mdA.totalOutstanding - mdB.totalOutstanding,
      defaultSortOrder: 'descend',
    },
  ];

  const mdGetRowClassName = (mdRecord: ArSummaryItem): string => {
    if (mdRecord.days90Plus > 0) return 'ar-row-critical';
    if (mdRecord.days61To90 > 0 || mdRecord.days31To60 > 0) return 'ar-row-warning';
    return '';
  };

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Дебіторська заборгованість
      </Typography.Title>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Всього заборгованість"
              value={mdTotals.mdTotalDebt}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="UAH"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Поточна"
              value={mdTotals.mdCurrentDebt}
              precision={2}
              prefix={<ClockCircleOutlined />}
              suffix="UAH"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Прострочена"
              value={mdTotals.mdOverdueDebt}
              precision={2}
              prefix={<WarningOutlined />}
              suffix="UAH"
              valueStyle={{ color: mdTotals.mdOverdueDebt > 0 ? '#f5222d' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Aging distribution bar */}
      {mdAgingDistribution.length > 0 && (
        <Card size="small" style={{ marginBottom: 24 }}>
          <Typography.Text strong style={{ marginBottom: 8, display: 'block' }}>
            Розподіл заборгованості за строком
          </Typography.Text>
          <div style={{ display: 'flex', height: 32, borderRadius: 4, overflow: 'hidden' }}>
            {mdAgingDistribution.map((mdSegment) =>
              mdSegment.percent > 0 ? (
                <div
                  key={mdSegment.label}
                  style={{
                    width: `${mdSegment.percent}%`,
                    backgroundColor: mdSegment.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 12,
                    minWidth: mdSegment.percent > 5 ? undefined : 0,
                  }}
                  title={`${mdSegment.label}: ${mdSegment.amount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })} (${mdSegment.percent.toFixed(1)}%)`}
                >
                  {mdSegment.percent > 8 ? mdSegment.label : ''}
                </div>
              ) : null
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
            {mdAgingDistribution.map((mdSegment) => (
              <div key={mdSegment.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: mdSegment.color }} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {mdSegment.label}: {mdSegment.amount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
                </Typography.Text>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Table<ArSummaryItem>
        columns={mdColumns}
        dataSource={mdArData}
        rowKey="customerId"
        rowClassName={mdGetRowClassName}
        pagination={false}
        size="middle"
        locale={{ emptyText: 'Заборгованість відсутня' }}
      />

      <style>{`
        .ar-row-critical td {
          background-color: #fff2f0 !important;
        }
        .ar-row-critical:hover td {
          background-color: #ffebe8 !important;
        }
        .ar-row-warning td {
          background-color: #fffbe6 !important;
        }
        .ar-row-warning:hover td {
          background-color: #fff7cc !important;
        }
      `}</style>
    </>
  );
}
